import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';

/** Smallest height a dragged widget can be set to. */
const HEIGHT_DRAG_MIN = 48;

/** Height change per arrow-key press. */
const HEIGHT_KEY_STEP = 8;

/** Applies a dragged height (px); undefined fits the widget to its content again. */
export type ApplyHeight = (px: number | undefined) => void;

/**
 * Bottom strip on a widget in a fit-to-content block or section. Dragging sets the widget's height in
 * 8 px steps (see beginWidgetHeight in the builder context for what that does to the rows around it);
 * double-click fits it to its content again. Mount it as the last child of the widget's card.
 *
 * `begin` is called when a drag starts and returns the function that applies each dragged height, so
 * the cell can be measured once, at the start.
 */
export function RowHeightHandle({ begin }: { begin: () => ApplyHeight }): React.ReactElement {
  const drag = useRef<{ y: number; height: number; apply: ApplyHeight } | null>(null);
  const heightAt = (clientY: number) => {
    const d = drag.current;
    return d ? Math.max(HEIGHT_DRAG_MIN, Math.round((d.height + clientY - d.y) / 8) * 8) : null;
  };
  return (
    <Edge
      role="separator"
      aria-orientation="horizontal"
      aria-label="Widget height"
      tabIndex={0}
      title="Drag to set this widget's height, or use the arrow keys; double-click or press Enter to fit content"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          begin()(undefined);
          return;
        }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        e.stopPropagation();
        const height = e.currentTarget.parentElement?.getBoundingClientRect().height ?? 0;
        const step = e.key === 'ArrowDown' ? HEIGHT_KEY_STEP : -HEIGHT_KEY_STEP;
        begin()(Math.max(HEIGHT_DRAG_MIN, Math.round((height + step) / HEIGHT_KEY_STEP) * HEIGHT_KEY_STEP));
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        begin()(undefined);
      }}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const card = e.currentTarget.parentElement;
        drag.current = { y: e.clientY, height: card?.getBoundingClientRect().height ?? 0, apply: begin() };
      }}
      onPointerMove={(e) => {
        const px = heightAt(e.clientY);
        if (px !== null) drag.current?.apply(px);
      }}
      onPointerUp={(e) => {
        const px = heightAt(e.clientY);
        if (px !== null) drag.current?.apply(px);
        drag.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
    />
  );
}

const Edge = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '10px',
  cursor: 's-resize',
  zIndex: 2,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover, &:focus-visible': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset 0 -3px 0 0 ${theme.palette.primary.main}`,
  },
  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
}));
